import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { publicSignup } from '../../services/api';
import { useAuth, SESSION_MODES } from '../../context/AuthContext';
import PhoneInput from '../../components/PhoneInput';

/**
 * SignupPage — fluxo self-serve do plano Starter (Frente B).
 *
 * Valida localmente o form, chama POST /api/public/signup, redireciona
 * pra /login no sucesso. Planos superiores (Essencial+) continuam indo
 * pelo formulário de lead da landing (#contato) porque exigem validação
 * manual do WhatsApp Business API / Meta.
 */
export default function SignupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    business_name: '',
    business_type: '',
    phone: '',
    admin_name: '',
    email: '',
    password: '',
    password_confirm: '',
    accept_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Preço dinâmico do Starter — carrega da API pública que já inclui
  // campanhas de desconto ativas. Se a API falhar, usa fallback hardcoded.
  const [starterPrice, setStarterPrice] = useState(null);
  const [campaignLabel, setCampaignLabel] = useState(null);

  useEffect(() => {
    fetch('/api/public/plans')
      .then(r => r.ok ? r.json() : null)
      .then(res => {
        const plans = res?.data?.plans || res?.plans || [];
        const starter = plans.find(p => (p.name || '').toLowerCase() === 'starter');
        if (starter) {
          // Se tem preço promocional (campanha), mostra riscado + promo
          const monthly = starter.prices?.monthly;
          if (monthly?.has_campaign_discount && monthly.original != null) {
            setStarterPrice({
              original: parseFloat(monthly.original),
              promo: parseFloat(monthly.amount),
            });
            setCampaignLabel(starter.campaign_label || null);
          } else {
            setStarterPrice({ original: parseFloat(monthly?.amount || starter.price || 49), promo: null });
          }
        }
      })
      .catch(() => {});
  }, []);

  // Usuários já autenticados são redirecionados — /signup não faz sentido
  // pra quem já está logado.
  useEffect(() => {
    if (user) navigate(user.mode === SESSION_MODES.MASTER_GLOBAL ? '/master' : '/app', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    // Validações client-side. O backend revalida tudo, mas damos feedback
    // imediato pra não bater na API por erro óbvio.
    if (!form.business_name.trim() || form.business_name.trim().length < 2) {
      setError('Informe o nome do seu negócio (mínimo 2 caracteres).');
      return;
    }
    if (!form.admin_name.trim() || form.admin_name.trim().length < 2) {
      setError('Informe seu nome completo.');
      return;
    }
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Informe um e-mail válido.');
      return;
    }
    if (form.password.length < 8) {
      setError('A senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (form.password !== form.password_confirm) {
      setError('As senhas não conferem.');
      return;
    }
    if (!form.accept_terms) {
      setError('Você precisa aceitar os termos para continuar.');
      return;
    }

    setLoading(true);
    try {
      await publicSignup({
        business_name: form.business_name.trim(),
        business_type: form.business_type.trim() || undefined,
        phone: form.phone.trim() || undefined,
        admin_name: form.admin_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        plan_slug: 'starter',
        accept_terms: true,
        source: 'signup-page',
      });
      toast.success('Conta criada! Faça login para continuar.', { duration: 6000 });
      // Redireciona pro login com o email pré-preenchido via query string —
      // o LoginPage aceita mas precisaria de uma pequena tweak para ler.
      // Por ora, só redireciona e o usuário digita de novo.
      navigate('/login', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Erro ao criar conta. Tente novamente em instantes.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box" style={{ maxWidth: 460 }}>
        <div className="login-logo">
          <img src="/logo-full.svg" alt="AtendoApp" style={{ height: 42, objectFit: 'contain', marginBottom: 16 }} />
          <h1 style={{
            fontSize: 22,
            fontWeight: 800,
            lineHeight: 1.2,
            letterSpacing: '-0.4px',
            margin: '0 0 6px 0',
            color: 'var(--gray-900)',
          }}>
            Crie sua conta grátis
          </h1>
          <p style={{
            fontSize: 13.5,
            color: 'var(--gray-500)',
            fontWeight: 500,
            margin: 0,
            lineHeight: 1.5,
          }}>
            14 dias de teste completo • No ar em 1 minuto
          </p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="badge-danger" style={{ borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, display: 'block', fontWeight: 500 }}>
              ⚠️ {error}
            </div>
          )}

          <div className="form-group">
            <label>Nome do seu negócio *</label>
            <input
              type="text"
              placeholder="Ex: Clínica Bem Estar"
              value={form.business_name}
              onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
              autoFocus
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label>Tipo de negócio</label>
            <select
              value={form.business_type}
              onChange={e => setForm(f => ({ ...f, business_type: e.target.value }))}
            >
              <option value="">Selecione…</option>
              <option value="salao">Salão de beleza / barbearia</option>
              <option value="estetica">Estética / spa</option>
              <option value="clinica">Clínica / consultório</option>
              <option value="fisioterapia">Fisioterapia / reabilitação</option>
              <option value="psicologia">Psicologia / terapia</option>
              <option value="nutricao">Nutrição</option>
              <option value="dentista">Odontologia</option>
              <option value="pet">Pet shop / banho e tosa</option>
              <option value="outro">Outro</option>
            </select>
          </div>

          <div className="form-group">
            <label>Seu nome completo *</label>
            <input
              type="text"
              placeholder="Como você quer ser chamado(a)"
              value={form.admin_name}
              onChange={e => setForm(f => ({ ...f, admin_name: e.target.value }))}
              autoComplete="name"
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label>E-mail *</label>
            <input
              type="email"
              placeholder="seu@email.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              autoComplete="email"
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label>WhatsApp / Telefone</label>
            <PhoneInput
              value={form.phone}
              onChange={(masked, normalized) => setForm(f => ({ ...f, phone: normalized || masked }))}
              placeholder="(11) 91234-5678"
            />
          </div>

          <div className="form-row" style={{ display: 'flex', gap: 8 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Senha *</label>
              <input
                type="password"
                placeholder="Mínimo 8 caracteres"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Confirmar *</label>
              <input
                type="password"
                placeholder="Repita"
                value={form.password_confirm}
                onChange={e => setForm(f => ({ ...f, password_confirm: e.target.value }))}
                autoComplete="new-password"
                minLength={8}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 12, color: 'var(--gray-600)', fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={form.accept_terms}
                onChange={e => setForm(f => ({ ...f, accept_terms: e.target.checked }))}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <span>
                Aceito os <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Termos de Uso</a> e a <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Política de Privacidade</a>.
              </span>
            </label>
          </div>

          <button
            type="submit"
            className={`btn btn-primary${loading ? ' btn-loading' : ''}`}
            style={{ width: '100%', padding: 11, fontSize: 14, marginTop: 12 }}
            disabled={loading}
          >
            {loading ? 'Criando conta…' : '🚀 Criar minha conta Starter grátis'}
          </button>
        </form>

        <div style={{ marginTop: 18, padding: 12, background: 'var(--gray-50)', borderRadius: 8, fontSize: 11, color: 'var(--gray-600)', lineHeight: 1.5 }}>
          {starterPrice?.promo ? (
            <>
              {campaignLabel && <span style={{ background: 'linear-gradient(135deg,#f59e0b,#dc2626)', color: '#fff', fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 8, marginRight: 6 }}>{campaignLabel}</span>}
              ⭐ <strong>Plano Starter</strong> — <span style={{ textDecoration: 'line-through', color: 'var(--gray-400)' }}>R$ {starterPrice.original.toFixed(2).replace('.', ',')}/mês</span>{' '}
              <strong style={{ color: '#16a34a' }}>R$ {starterPrice.promo.toFixed(2).replace('.', ',')}/mês</strong> após o trial de 14 dias.
            </>
          ) : (
            <>
              ⭐ <strong>Plano Starter</strong> — R$ {(starterPrice?.original || 49).toFixed(2).replace('.', ',')}/mês após o trial de 14 dias.
            </>
          )}
          {' '}Inclui até 5 usuários, 100 agendamentos/mês, página pública de agendamento, CRM básico
          e lembretes por e-mail. Para WhatsApp automático e planos superiores, <a href="/#contato" style={{ color: 'var(--primary)' }}>fale com nosso time</a>.
        </div>

        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13 }}>
          Já tem uma conta? <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>Entrar</Link>
        </p>
        <p style={{ textAlign: 'center', marginTop: 10, fontSize: 12 }}>
          <a href="/" style={{ color: 'var(--gray-500)', textDecoration: 'none' }}>
            ← Voltar para atendoapp.com
          </a>
        </p>
      </div>
    </div>
  );
}
